import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth } from '@/lib/api'
import { isRecord, parseFactsSchema } from './facts'
import type {
  OrgWorkflow,
  OrgWorkflowsResponse,
  WorkflowRecord,
  WorkflowsResponse,
} from './types'

const API_BASE = `${AGENT_URL}/data`

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/**
 * The workflows the caller's org can run, from Nebula via Hoku. The org comes
 * from the auth token server-side, so there is nothing to pass in.
 *
 * Fields are narrowed rather than trusted: Nebula owns the source of truth and
 * may add keys, and a workflow whose YAML it can't read comes back with null
 * metadata.
 */
export async function fetchOrgWorkflows(
  token: string | null
): Promise<OrgWorkflow[]> {
  const response = await fetchJsonWithAuth<OrgWorkflowsResponse>({
    token: token ?? '',
    url: `${API_BASE}/workflows/available`,
    errorMessage: 'Failed to fetch available workflows',
  })

  const workflows = Array.isArray(response.workflows) ? response.workflows : []
  const parsed: OrgWorkflow[] = []
  for (const workflow of workflows) {
    if (!isRecord(workflow) || typeof workflow.slug !== 'string') continue
    parsed.push({
      slug: workflow.slug,
      org_slug: asString(workflow.org_slug),
      title: asString(workflow.title),
      trigger: asString(workflow.trigger),
      description: asString(workflow.description),
      // Only an explicit `true` retires a workflow, so a payload predating the
      // field reads as live rather than deprecated.
      deprecated: workflow.deprecated === true,
      // Same explicit-`true` rule: a payload predating the field means no
      // workflow has opted in to providing an in-call tool, not that all have.
      provides_in_call_tool: workflow.provides_in_call_tool === true,
      facts_schema: parseFactsSchema(workflow.facts_schema),
    })
  }
  return parsed
}

export async function fetchWorkflows(
  token: string | null
): Promise<WorkflowRecord[]> {
  const response = await fetchJsonWithAuth<WorkflowsResponse>({
    token: token ?? '',
    url: `${API_BASE}/workflows`,
    errorMessage: 'Failed to fetch workflows',
  })

  // Guarantee `duties` is always an array so consumers can map over it safely,
  // even if an older cached payload predates the field. Return new objects
  // rather than mutating in place — these land in the react-query cache.
  return (response.data ?? []).map((workflow) => ({
    ...workflow,
    duties: Array.isArray(workflow.duties)
      ? workflow.duties.map((duty) => ({
          id: duty.id,
          name: duty.name,
          // Only an explicit false is off, matching the server: a payload
          // predating the junction's flag must not read as switched off.
          active: duty.active !== false,
        }))
      : [],
    session_count:
      typeof workflow.session_count === 'number' ? workflow.session_count : 0,
  }))
}
